import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Loader2, RotateCcw, Save, Send } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { TextF, AreaF, CheckF, ImageF } from "./AFields";
import { RichTextF, toHtml } from "./RichText";
import { VersionHistory } from "./Versions";

const CONTENT_TABS = [
  {
    key: "hero", label: "Homepage Hero", contentType: "article",
    note: "Fallback hero content used when no custom hero slides exist.",
    fields: [
      { k: "heading_en", l: "Hero Heading (English)", t: "text" },
      { k: "heading_hi", l: "Hero Heading (Hindi)", t: "text" },
      { k: "sub_en", l: "Hero Subtext (English)", t: "area", rows: 2, ai: true },
      { k: "sub_hi", l: "Hero Subtext (Hindi)", t: "area", rows: 2, ai: true },
      { k: "image", l: "Hero Image", t: "image" },
    ],
  },
  {
    key: "about", label: "About Page", contentType: "article",
    note: "Rich text, autosaved drafts, preview and publish controls.",
    fields: [
      ["intro", "Introduction"], ["vision", "Vision"], ["mission", "Mission"], ["objectives", "Objectives"],
      ["approach", "Our Approach"], ["leadership", "Leadership"], ["transparency", "Transparency"],
      ["values", "Core Values"], ["history", "History"], ["chairman", "Chairman's Message"], ["director", "Director's Message"],
    ].flatMap(([k, l]) => [
      { k: `${k}_en`, l: `${l} (English)`, t: "rich", ai: true },
      { k: `${k}_hi`, l: `${l} (Hindi)`, t: "rich", ai: true },
    ]),
  },
];

const TABS = [...CONTENT_TABS.map((t) => t.label), "Organisation Settings", "Email (SMTP)", "Payment (Cashfree)", "Change Password"];

function EditorField({ field, value, onChange, contentType }) {
  if (field.t === "rich") return <RichTextF label={field.l} value={value} onChange={onChange} ai={field.ai} contentType={contentType} />;
  if (field.t === "area") return <AreaF label={field.l} value={value} onChange={onChange} rows={field.rows || 4} ai={field.ai} contentType={contentType} />;
  if (field.t === "image") return <ImageF label={field.l} value={value} onChange={onChange} />;
  return <TextF label={field.l} value={value} onChange={onChange} />;
}

function PreviewValue({ field, value }) {
  if (field.t === "image") return value ? <img src={value} alt="" className="max-h-48 rounded-lg object-cover" /> : <p className="text-sm text-slate-400">No image selected</p>;
  if (field.t === "rich" || field.t === "area") {
    return <div className="prose-ssf max-w-none text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: toHtml(value) }} />;
  }
  return <p className="text-sm font-semibold text-navy">{value || "—"}</p>;
}

function ContentEditor({ cfg, refreshSite }) {
  const [values, setValues] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState("");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState("");
  const valuesRef = useRef(null);
  const dirtyRef = useRef(false);
  const hasDraftRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/content/${cfg.key}`);
      const { _has_draft, ...data } = r.data || {};
      setValues(data);
      setHasDraft(!!_has_draft);
      setDirty(false);
      setSaveState(_has_draft ? "saved" : "");
      setPreview(false);
    } catch (e) { toast.error(errMsg(e)); }
  }, [cfg.key]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { valuesRef.current = values; }, [values]);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  useEffect(() => { hasDraftRef.current = hasDraft; }, [hasDraft]);

  useEffect(() => {
    const h = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  const saveDraft = useCallback(async (auto = false) => {
    const current = valuesRef.current;
    if (!current || (auto && !dirtyRef.current)) return;
    if (!auto) setBusy("draft");
    setSaveState("saving");
    try {
      await api.put(`/admin/content/${cfg.key}`, current);
      setHasDraft(true);
      setDirty(false);
      setSaveState("saved");
      if (!auto) toast.success("Draft saved — the live website is unchanged");
    } catch (e) {
      setSaveState("");
      if (!auto) toast.error(errMsg(e));
    } finally { if (!auto) setBusy(""); }
  }, [cfg.key]);

  useEffect(() => {
    const iv = setInterval(() => saveDraft(true), 20000);
    return () => clearInterval(iv);
  }, [saveDraft]);

  const publish = async () => {
    if (!dirtyRef.current && !hasDraftRef.current) {
      toast.info("No unpublished draft changes");
      return;
    }
    setBusy("publish");
    try {
      await api.put(`/admin/content/${cfg.key}`, valuesRef.current || {});
      await api.post(`/admin/content/${cfg.key}/publish`);
      toast.success("Published — live on the website now");
      setHasDraft(false);
      setDirty(false);
      setSaveState("");
      await load();
      refreshSite();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const discard = async () => {
    if (!window.confirm("Discard the unpublished draft and restore the live content in this editor?")) return;
    setBusy("discard");
    try {
      await api.post(`/admin/content/${cfg.key}/discard`);
      toast.success("Draft discarded");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!values) return <div className="flex items-center gap-2 py-10 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  const setField = (k) => (v) => {
    setValues({ ...valuesRef.current, [k]: v });
    setDirty(true);
    setSaveState("");
  };

  return (
    <div className="space-y-5" data-testid={`${cfg.key}-editor`}>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-saffron-500/30 bg-saffron-100/50 px-4 py-3">
        <div>
          <p className="text-sm font-bold text-navy">{cfg.note}</p>
          <p className="mt-0.5 text-xs text-slate-600">Draft → Preview → Publish. Autosave runs every 20 seconds while editing.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase ${hasDraft || dirty ? "bg-saffron-500 text-navy" : "bg-brandgreen-100 text-brandgreen"}`} data-testid={`${cfg.key}-draft-state`}>
            {saveState === "saving" ? "Saving draft…" : dirty ? "Unsaved changes" : hasDraft ? "Draft ready" : "Live"}
          </span>
          <VersionHistory coll="content" itemId={cfg.key} onRestored={load} hasDraft={hasDraft || dirty} />
        </div>
      </div>

      {cfg.key === "hero" && (
        <p className="rounded-lg border border-gray-200 bg-navy-50 px-4 py-3 text-sm text-slate-600">
          Manage the multi-slide homepage carousel in <Link to="/admin/hero" className="font-bold text-saffron-600 hover:text-navy" data-testid="hero-manager-link">Home Hero</Link>. These fields remain the safe fallback.
        </p>
      )}

      <div className="grid gap-5">
        {cfg.fields.map((f) => <EditorField key={f.k} field={f} value={values[f.k]} onChange={setField(f.k)} contentType={cfg.contentType} />)}
      </div>

      {preview && (
        <div className="rounded-lg border border-navy/15 bg-navy-50/50 p-5" data-testid={`${cfg.key}-preview`}>
          <p className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-500">Draft preview</p>
          <div className="space-y-5">
            {cfg.fields.map((f) => (
              <div key={f.k}>
                <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-saffron-600">{f.l}</p>
                <PreviewValue field={f} value={values[f.k]} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-5">
        {hasDraft && (
          <button onClick={discard} disabled={!!busy} className="rounded-md border border-red-200 px-5 py-2.5 text-sm font-semibold text-red-500 disabled:opacity-50" data-testid={`${cfg.key}-discard-draft`}>
            <RotateCcw size={14} /> Discard Draft
          </button>
        )}
        <button onClick={() => setPreview(!preview)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy" data-testid={`${cfg.key}-preview-btn`}>
          <Eye size={14} /> {preview ? "Hide Preview" : "Preview"}
        </button>
        <button onClick={() => saveDraft(false)} disabled={!!busy} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid={`${cfg.key}-save-draft`}>
          {busy === "draft" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Draft
        </button>
        <button onClick={publish} disabled={!!busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid={`${cfg.key}-publish`}>
          {busy === "publish" ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />} Publish
        </button>
      </div>
    </div>
  );
}

export default function Content() {
  const { setSite } = useApp();
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const refreshSite = useCallback(() => {
    api.get("/public/site").then((r) => setSite(r.data)).catch(() => {});
  }, [setSite]);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const save = async (fn, label = "Saved") => {
    setBusy(true);
    try { await fn(); toast.success(label); refreshSite(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const setS = (k) => (v) => setSettings({ ...settings, [k]: v });
  const setNested = (obj, k) => (v) => setSettings({ ...settings, [obj]: { ...(settings[obj] || {}), [k]: v } });

  const SaveBtn = ({ onClick, testid }) => (
    <button onClick={onClick} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid={testid}>
      {busy && <Loader2 size={15} className="animate-spin" />} Save
    </button>
  );

  return (
    <div data-testid="admin-content">
      <SEO title="Site Content & Settings" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Site Content & Settings</h1>
      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map((t2, i) => (
          <button key={t2} onClick={() => setTab(i)} data-testid={`content-tab-${i}`}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === i ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>{t2}</button>
        ))}
      </div>

      <div className="mt-6 max-w-4xl rounded-lg border border-gray-200 bg-white p-6 sm:p-8">
        {tab < CONTENT_TABS.length && <ContentEditor key={CONTENT_TABS[tab].key} cfg={CONTENT_TABS[tab]} refreshSite={refreshSite} />}
        {tab === 2 && settings && (
          <div className="space-y-5" data-testid="settings-editor">
            <div className="grid gap-5 sm:grid-cols-2">
              <TextF label="Phone" value={settings.phone} onChange={setS("phone")} />
              <TextF label="WhatsApp Number (with country code)" value={settings.whatsapp} onChange={setS("whatsapp")} />
              <TextF label="Email" value={settings.email} onChange={setS("email")} />
              <TextF label="Website" value={settings.website} onChange={setS("website")} />
            </div>
            <TextF label="Address" value={settings.address_line} onChange={setS("address_line")} />
            <TextF label="Google Maps Embed URL" value={settings.map_embed_url} onChange={setS("map_embed_url")} />
            <div className="grid gap-5 sm:grid-cols-2">
              <TextF label="UDYAM Registration" value={settings.udyam} onChange={setS("udyam")} />
              <TextF label="NGO DARPAN Unique ID" value={settings.darpan_id} onChange={setS("darpan_id")} />
              <TextF label="Divyangjan Certificate" value={settings.divyangjan_cert} onChange={setS("divyangjan_cert")} />
              <TextF label="12A Registration (leave blank if not held)" value={settings.tax_12a} onChange={setS("tax_12a")} />
              <TextF label="80G Registration (leave blank if not held)" value={settings.tax_80g} onChange={setS("tax_80g")} />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {["facebook", "instagram", "x", "linkedin", "youtube"].map((k) => (
                <TextF key={k} label={`${k.charAt(0).toUpperCase() + k.slice(1)} URL`} value={settings.social?.[k]} onChange={setNested("social", k)} />
              ))}
            </div>
            <CheckF label="Show Today's Greeting on homepage" value={settings.show_home_greeting} onChange={setS("show_home_greeting")} />
            <SaveBtn testid="settings-save" onClick={() => save(() => api.put("/admin/settings", settings), "Settings updated")} />
          </div>
        )}
        {tab === 3 && settings && (
          <div className="space-y-5" data-testid="smtp-editor">
            <p className={`rounded-lg px-4 py-2.5 text-xs font-bold ${settings.smtp?.host ? "bg-brandgreen-100 text-brandgreen" : "bg-saffron-100 text-saffron-600"}`} data-testid="smtp-status">
              {settings.smtp?.host ? "Email is configured" : "Email NOT CONFIGURED — forms will keep working and submissions are saved in the database."}
            </p>
            <div className="grid gap-5 sm:grid-cols-2">
              <TextF label="SMTP Host" value={settings.smtp?.host} onChange={setNested("smtp", "host")} />
              <TextF label="SMTP Port" type="number" value={settings.smtp?.port} onChange={(v) => setNested("smtp", "port")(Number(v) || 587)} />
              <TextF label="SMTP User" value={settings.smtp?.user} onChange={setNested("smtp", "user")} />
              <TextF label="SMTP Password (leave blank to keep)" type="password" value={settings.smtp?.password} onChange={setNested("smtp", "password")} />
              <TextF label="From Email" value={settings.smtp?.from_email} onChange={setNested("smtp", "from_email")} />
            </div>
            <SaveBtn testid="smtp-save" onClick={() => save(() => api.put("/admin/settings", settings), "Email settings saved")} />
          </div>
        )}
        {tab === 4 && settings && (
          <div className="space-y-5" data-testid="cashfree-editor">
            <p className={`rounded-lg px-4 py-2.5 text-xs font-bold ${settings.cashfree?.secret_set ? "bg-brandgreen-100 text-brandgreen" : "bg-saffron-100 text-saffron-600"}`} data-testid="cashfree-status">
              {settings.cashfree?.secret_set ? "Cashfree is configured" : "Cashfree NOT CONFIGURED — donations are recorded as 'Initiated' and no payment is charged. No fake transactions are created."}
            </p>
            <div className="grid gap-5 sm:grid-cols-2">
              <TextF label="Cashfree App ID" value={settings.cashfree?.app_id} onChange={setNested("cashfree", "app_id")} />
              <TextF label="Cashfree Secret Key (leave blank to keep)" type="password" value={settings.cashfree?.secret_key} onChange={setNested("cashfree", "secret_key")} />
            </div>
            <SaveBtn testid="cashfree-save" onClick={() => save(() => api.put("/admin/settings", settings), "Payment settings saved")} />
          </div>
        )}
        {tab === 5 && (
          <div className="max-w-md space-y-5" data-testid="password-editor">
            <TextF label="Current Password" type="password" value={pw.current_password} onChange={(v) => setPw({ ...pw, current_password: v })} />
            <TextF label="New Password (min 8 chars)" type="password" value={pw.new_password} onChange={(v) => setPw({ ...pw, new_password: v })} />
            <SaveBtn testid="password-save" onClick={() => save(async () => { await api.post("/auth/change-password", pw); setPw({ current_password: "", new_password: "" }); }, "Password changed")} />
          </div>
        )}
      </div>
    </div>
  );
}
